import React, { useCallback } from 'react';
import { useState } from 'react';
import { useMemo } from 'react';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { z } from 'zod';
import { CircularProgress } from '@mui/material';

export const DataSetVersionFileSelector = ({
  organizationId,
  databaseId,
  dataSetId,
  dataSetVersion,
  automationTag,
  initialFileInfo,
}: {
  organizationId: string;
  databaseId: string;
  dataSetId: string;
  dataSetVersion: string;
  automationTag: string | null;
  initialFileInfo: {
    fileConsoleUrls: Record<string, string>;
    totalNumberOfFiles: number;
  };
}) => {
  const [showAllFiles, setShowAllFiles] = useState<boolean>(false);

  const listDataSetVersionFileNamesUrl = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/data-sets/${dataSetId}/versions/${dataSetVersion}/files`;
  }, [organizationId, databaseId, dataSetId, dataSetVersion]);

  const listDataSetVersionFileNamesReadPattern = useSimpleReadPattern({
    getUrlFn: listDataSetVersionFileNamesUrl,
    responseSchema: z.record(z.string(), z.string()),
    canRead: showAllFiles,
  });

  const allFileConsoleUrls = useMemo(() => {
    if (listDataSetVersionFileNamesReadPattern.status === 'done') {
      return listDataSetVersionFileNamesReadPattern.entity;
    } else {
      return initialFileInfo.fileConsoleUrls;
    }
  }, [listDataSetVersionFileNamesReadPattern, initialFileInfo]);

  const filesToDisplay = useMemo(() => {
    return showAllFiles
      ? Object.keys(allFileConsoleUrls)
      : Object.keys(allFileConsoleUrls).slice(0, 5);
  }, [showAllFiles, allFileConsoleUrls]);

  const seeButtonsNeeded = useMemo(() => {
    return Object.keys(allFileConsoleUrls).length > 5;
  }, [allFileConsoleUrls]);

  const allTotalNumberOfFiles = useMemo(() => {
    if (listDataSetVersionFileNamesReadPattern.status === 'done') {
      return Object.keys(listDataSetVersionFileNamesReadPattern.entity).length;
    } else {
      return initialFileInfo.totalNumberOfFiles;
    }
  }, [listDataSetVersionFileNamesReadPattern, initialFileInfo]);

  const ui: React.ReactNode = useMemo(() => {
    return (
      <div>
        {listDataSetVersionFileNamesReadPattern.status === 'loading' &&
          showAllFiles && (
            <div>
              <CircularProgress size={32} thickness={8} />
            </div>
          )}
        {(listDataSetVersionFileNamesReadPattern.status !== 'loading' ||
          !showAllFiles) && [
          ...filesToDisplay.map((fileName) => {
            const fileConsoleUrl = allFileConsoleUrls[fileName] ?? '#';

            return (
              <p key={`${automationTag ? `${automationTag}_` : ''}${fileName}`}>
                <a
                  href={fileConsoleUrl}
                  target={'_blank'}
                  rel={'noreferrer'}
                  className={'text-blue-500 underline'}
                >{`${automationTag ? `${automationTag}_` : ''}${fileName}`}</a>
              </p>
            );
          }),
          !showAllFiles && seeButtonsNeeded && (
            <p key={'see_all'}>
              <a
                href={'#'}
                target={'_blank'}
                rel={'noreferrer'}
                className={'text-blue-500 underline'}
                onClick={(e) => {
                  e.preventDefault();
                  setShowAllFiles(true);
                }}
              >
                {`See all (${allTotalNumberOfFiles})`}
              </a>
            </p>
          ),
          showAllFiles && seeButtonsNeeded && (
            <p key={'see_less'}>
              <a
                href={'#'}
                target={'_blank'}
                rel={'noreferrer'}
                className={'text-blue-500 underline'}
                onClick={(e) => {
                  e.preventDefault();
                  setShowAllFiles(false);
                }}
              >
                {'See less...'}
              </a>
            </p>
          ),
        ]}
      </div>
    );
  }, [
    filesToDisplay,
    allFileConsoleUrls,
    showAllFiles,
    seeButtonsNeeded,
    dataSetVersion,
    allTotalNumberOfFiles,
    setShowAllFiles,
    listDataSetVersionFileNamesReadPattern,
  ]);

  return ui;
};
